import React, { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Flag, ShieldAlert, Calendar, Building2, Check, X, Ban } from 'lucide-react';
import { toast } from 'sonner';
import FlagViolationModal from './FlagViolationModal';

const SEVERITY_STYLE = {
  minor: 'bg-amber-100 text-amber-800 border-amber-200',
  major: 'bg-orange-100 text-orange-800 border-orange-200',
  severe: 'bg-red-100 text-red-800 border-red-200',
};
const CATEGORY_LABEL = {
  no_show: 'No-show', left_early: 'Left early', late: 'Late', unprofessional: 'Unprofessional',
  dress_code: 'Dress code', solicitation: 'Solicited tips/money/favors', off_platform: 'Off-platform',
  falsified_records: 'Falsified records', incomplete_media: 'Incomplete media', safety: 'Safety', other: 'Other',
};
const fmtDate = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

/**
 * BookingViolationModal — opens when an admin clicks a past booking on the
 * talent profile. Shows the event/client/date, any existing CoC violations
 * for that booking, and a "Flag Code of Conduct Violation" action. Works
 * retroactively for completed (incl. pre-feature) events.
 */
export const BookingViolationModal = ({ open, onClose, job, talentId, talentName, onChanged }) => {
  const [violations, setViolations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flagOpen, setFlagOpen] = useState(false);
  const [blockInfo, setBlockInfo] = useState({ client_blocked: false, client_id: null, client_name: null });

  const load = useCallback(async () => {
    if (!job?.booking_id) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/bookings/${job.booking_id}/coc-violations`);
      setViolations(r.data?.violations || []);
      setBlockInfo({ client_blocked: !!r.data?.client_blocked, client_id: r.data?.client_id, client_name: r.data?.client_name });
    } catch (e) { setViolations([]); } finally { setLoading(false); }
  }, [job]);

  useEffect(() => { if (open && job?.booking_id) load(); }, [open, job, load]);

  const unblock = async () => {
    if (!blockInfo.client_id) return;
    if (!window.confirm(`Unblock this talent from ${blockInfo.client_name || 'this client'}? They will be able to see and apply to this client's gigs again.`)) return;
    try {
      await api.post('/admin/coc/unblock-client', { talent_id: talentId, client_id: blockInfo.client_id });
      toast.success('Talent unblocked from client');
      load(); onChanged?.();
    } catch (e) { toast.error('Unblock failed'); }
  };

  const update = async (id, status) => {
    const note = window.prompt(status === 'dismissed' ? 'Reason for dismissing (optional):' : 'Resolution note (optional):') || '';
    try {
      await api.patch(`/admin/coc-violations/${id}`, { status, resolution_note: note });
      toast.success(status === 'resolved' ? 'Marked resolved' : 'Dismissed');
      load(); onChanged?.();
    } catch (e) { toast.error('Update failed'); }
  };

  if (!job) return null;
  const eventLabel = `${job.event_name || 'Event'} · ${fmtDate(job.date_worked)}`;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-lg" data-testid="booking-violation-modal">
        <DialogHeader>
          <DialogTitle className="text-base">{job.event_name || 'Event'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-1 text-sm text-slate-600 mb-2">
          <div className="flex items-center gap-2"><Building2 className="w-3.5 h-3.5 text-slate-400" /> {job.client_name || '—'}</div>
          <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-slate-400" /> {fmtDate(job.date_worked)}{job.role ? ` · ${job.role}` : ''}</div>
          {job.status && <Badge variant="outline" className="text-[10px] capitalize">{String(job.status).replace(/_/g, ' ')}</Badge>}
        </div>

        {blockInfo.client_blocked && (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" data-testid="booking-client-blocked-banner">
            <Ban className="w-4 h-4 shrink-0" />
            <span className="flex-1">Blocked from {blockInfo.client_name || 'this client'} — hidden from their gigs.</span>
            <Button size="sm" variant="outline" className="h-7 text-xs border-red-300 text-red-700" onClick={unblock} data-testid="booking-unblock-client-btn">Unblock</Button>
          </div>
        )}

        <div className="border-t border-slate-100 pt-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
              <ShieldAlert className="w-3.5 h-3.5 text-red-500" /> Code of Conduct
            </span>
            <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white h-7 text-xs" onClick={() => setFlagOpen(true)} data-testid="booking-flag-violation-btn">
              <Flag className="w-3 h-3 mr-1" /> Flag Violation
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-5"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : violations.length === 0 ? (
            <p className="text-sm text-slate-400 py-2" data-testid="booking-no-violations">No violations recorded for this event.</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {violations.map((v) => (
                <div key={v.id} className="border border-slate-200 rounded-lg p-2.5" data-testid={`booking-violation-${v.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-800 text-sm">{CATEGORY_LABEL[v.category] || v.category}</span>
                    <Badge className={`text-[10px] border ${SEVERITY_STYLE[v.severity] || ''}`}>{v.severity}</Badge>
                    <Badge variant="outline" className={`text-[10px] ${v.status === 'open' ? 'text-red-700 border-red-300' : 'text-slate-500'}`}>{v.status}</Badge>
                    <span className="text-[11px] text-slate-400 ml-auto">{v.created_at ? new Date(v.created_at).toLocaleDateString() : ''}</span>
                  </div>
                  {v.client_complaint && <p className="text-sm text-slate-700 mt-1"><span className="text-slate-400 text-xs">Complaint:</span> {v.client_complaint}</p>}
                  {v.description && <p className="text-xs text-slate-500 mt-0.5">{v.description}</p>}
                  <p className="text-[11px] text-slate-400 mt-1">By {v.flagged_by_name || 'admin'}{v.coc_version_label ? ` · CoC ${v.coc_version_label}` : ''}</p>
                  {v.status !== 'open' && v.resolution_note && <p className="text-[11px] text-slate-500 italic mt-0.5">"{v.resolution_note}" — {v.resolved_by_name}</p>}
                  {v.status === 'open' && (
                    <div className="flex gap-2 mt-1.5">
                      <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => update(v.id, 'resolved')} data-testid={`booking-resolve-${v.id}`}><Check className="w-3 h-3 mr-1" /> Resolve</Button>
                      <Button size="sm" variant="ghost" className="h-6 text-[11px] text-slate-500" onClick={() => update(v.id, 'dismissed')} data-testid={`booking-dismiss-${v.id}`}><X className="w-3 h-3 mr-1" /> Dismiss</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <FlagViolationModal
          open={flagOpen}
          talentId={talentId}
          talentName={talentName}
          bookingId={job.booking_id}
          shiftId={job.shift_id}
          eventLabel={eventLabel}
          clientName={job.client_name || blockInfo.client_name}
          onClose={() => setFlagOpen(false)}
          onFlagged={() => { load(); onChanged?.(); }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default BookingViolationModal;
